import API from "../core/api";
import { CustomColorTheme, ThemeColorCategory } from "../resources/modelTypes";
import isEmpty from "lodash/isEmpty";
import React, {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";

export type CustomColorThemeCreateData = {
  [ThemeColorCategory.background]: string;
  [ThemeColorCategory.text]: string;
  [ThemeColorCategory.secondaryBackground]: string | null;
  [ThemeColorCategory.secondaryText]: string | null;
  [ThemeColorCategory.linkAndButtonText]: string;
  site: number;
};

export type CustomColorThemeUpdateData = {
  id: number;
  [ThemeColorCategory.background]?: string;
  [ThemeColorCategory.secondaryBackground]?: string | null;
  [ThemeColorCategory.text]?: string;
  [ThemeColorCategory.secondaryText]?: string | null;
  [ThemeColorCategory.linkAndButtonText]?: string;
};

type NullableCustomColorThemeUpdateData = CustomColorThemeUpdateData | null;

export type CustomColorThemesContextValue = {
  commitColorThemeUpdates: () => Promise<undefined>;
  createCustomColorTheme: (
    data: CustomColorThemeCreateData,
  ) => Promise<CustomColorTheme>;
  customColorThemes: CustomColorTheme[];
  deleteCustomColorTheme: (id: number) => Promise<void>;
  isLoading: boolean;
  setStagedColorThemeUpdates: (
    // This is the simple type definition for useState setter, it represents both
    // setting a new value directly or providing a function that receives the previous value
    update:
      | ((
          prevVal: NullableCustomColorThemeUpdateData,
        ) => NullableCustomColorThemeUpdateData)
      | NullableCustomColorThemeUpdateData,
  ) => void;
  stagedColorThemeUpdates: CustomColorThemeUpdateData | null;
};

export const CustomColorThemesContext =
  createContext<CustomColorThemesContextValue>({
    commitColorThemeUpdates: async () => undefined,
    createCustomColorTheme: async () => ({}) as CustomColorTheme,
    customColorThemes: [],
    deleteCustomColorTheme: async () => Promise.resolve(),
    isLoading: false,
    setStagedColorThemeUpdates: () => null,
    stagedColorThemeUpdates: null,
  });

export type CustomColorThemesProviderProps = {
  children: ReactNode;
  initialCustomColorThemes: CustomColorTheme[];
};

const CustomColorThemesProvider = ({
  children,
  initialCustomColorThemes = [],
}: CustomColorThemesProviderProps) => {
  const [customColorThemes, setCustomColorThemes] = useState<
    CustomColorTheme[]
  >(initialCustomColorThemes);
  const [isLoading, setIsLoading] = useState(false);
  const [stagedColorThemeUpdates, setStagedColorThemeUpdates] =
    useState<CustomColorThemeUpdateData | null>(null);

  // Hijack the state to inject whatever staged updates happen to be in the list
  const customColorThemesWithStagedUpdates = useMemo(() => {
    if (!stagedColorThemeUpdates) {
      return customColorThemes;
    }

    return customColorThemes.map((theme) => {
      if (theme.id !== stagedColorThemeUpdates.id) {
        return theme;
      }

      return {
        ...theme,
        ...stagedColorThemeUpdates,
      };
    });
  }, [customColorThemes, stagedColorThemeUpdates]);

  const createCustomColorTheme = useCallback(
    async (data: CustomColorThemeCreateData): Promise<CustomColorTheme> => {
      try {
        setIsLoading(true);

        const response = await API.post("custom-color-themes", {}, data);
        const newTheme = response as CustomColorTheme;

        setCustomColorThemes((prevThemes) => [...prevThemes, newTheme]);

        return newTheme;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const commitColorThemeUpdates = useCallback(async (): Promise<undefined> => {
    if (isEmpty(stagedColorThemeUpdates)) {
      return;
    }

    try {
      setIsLoading(true);

      // We know stagedColorThemeUpdates is not null here because this function should only be called when there are updates to commit
      const updateData = stagedColorThemeUpdates as CustomColorThemeUpdateData;
      const response = await API.patch(
        `custom-color-themes/${updateData.id}`,
        {},
        updateData,
      );
      const updatedTheme = response as CustomColorTheme;

      setCustomColorThemes((prevThemes) =>
        prevThemes.map((theme) =>
          theme.id === updateData.id ? updatedTheme : theme,
        ),
      );
      setStagedColorThemeUpdates(null);
    } finally {
      setIsLoading(false);
    }
  }, [stagedColorThemeUpdates]);

  const deleteCustomColorTheme = useCallback(
    async (id: number): Promise<void> => {
      try {
        setIsLoading(true);

        await API.delete(`custom-color-themes/${id}`, null, {});

        setCustomColorThemes((prevThemes) =>
          prevThemes.filter((theme) => theme.id !== id),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      commitColorThemeUpdates,
      createCustomColorTheme,
      customColorThemes: customColorThemesWithStagedUpdates,
      deleteCustomColorTheme,
      isLoading,
      setStagedColorThemeUpdates,
      stagedColorThemeUpdates,
    }),
    [
      commitColorThemeUpdates,
      createCustomColorTheme,
      customColorThemesWithStagedUpdates,
      deleteCustomColorTheme,
      isLoading,
      setStagedColorThemeUpdates,
      stagedColorThemeUpdates,
    ],
  );

  return (
    <CustomColorThemesContext.Provider value={contextValue}>
      {children}
    </CustomColorThemesContext.Provider>
  );
};

const MemoizedCustomColorThemesProvider = memo(CustomColorThemesProvider);

export { MemoizedCustomColorThemesProvider as CustomColorThemesProvider };
